<template>
  <page-outline 
    :title="$t('Webheader.annuaire_file')"
    :show-filter="true"
    :show-refresh="true"
    :filter-expanded="displayFilter"
    :refresh-disabled="activateSpinner"
    @toggle-filter="toggleFilter"
    @refresh="refreshAnnuaire">
    <template v-slot:primaryActions>
      <app-button
        v-if="canAddAnnuaireFile() && !activateSpinner"
        :text="$t('annuaire.add_annuaire_file')"
        icon="fa-light fa-plus"
        variant="primary"
        @click="addAnnuaireFile"
      />
    </template>
    
    <template slot="navigationTabs">
      <!-- Version mobile des tabs -->
      <div class="d-block d-lg-none">
        <div class="d-flex flex-column">
          <app-button
            v-for="(tab, index) in tabs"
            :key="index"
            :text="$t(tab.translationKey)"
            :variant="tabName === tab.value ? 'primary' : 'light'"
            size="md"
            :block="true"
            custom-class="mb-2"
            @click="changeTabName(tab.value)"
          />
        </div>
      </div>
      
      <!-- Version desktop des tabs -->
      <div class="d-none d-lg-block">
        <b-nav tabs fill class="border-0">
          <b-nav-item
            v-for="(tab, index) in tabs"
            :key="index"
            :active="tabName === tab.value"
            @click="changeTabName(tab.value)"
            :class="{'bg-primary text-white': tabName === tab.value}"
          >
            <span :class="{'font-weight-bold': tabName === tab.value}">
              {{ $t(tab.translationKey) }}
            </span>
          </b-nav-item>
        </b-nav>
      </div>
    </template>
    
    <template slot="filterContent">
      <app-panel
        :filters="filterConfig"
        :searchAriaLabel="$t('common.srch') + ' ' + $t('Webheader.annuaire_file')"
        @filter-changed="onFilterChanged"
        @clear-filters="clearFilterFields"
        @apply-filters="applyFilter"
      />
    </template>
    
    <template slot="pageContent">
      <b-container fluid>
        <div class="requestsList">
          <div class="mt-0">
            <AnnuaireLineList v-if="tabName === 1" 
              :type="TrackType" 
              :id="id"
              :showFilters="false"
              :filters="filterValues"
              @refresh="refreshInbox"></AnnuaireLineList>
            <AnnuaireFilePage v-if="tabName === 2"
              :showFilters="false"
              :filters="filterValues"
              @refresh="refreshInbox"></AnnuaireFilePage>
          </div>
        </div>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import AnnuaireFilePage from "@/pages/annuaire/AnnuaireFilePage.vue";
import AnnuaireLineList from "@/pages/annuaire/AnnuaireLineList.vue";
import AppButton from '@/components/common/AppButton.vue'
import AppPanel from '@/components/common/AppPanel.vue'
import {loadPage} from "@/helpers/routing";


export default {
  name: 'AnnuairePage',
  components: {
    AnnuaireLineList,
    AnnuaireFilePage,
    'page-outline': PageOutline,
    'app-button': AppButton,
    'app-panel': AppPanel
  },
  props: {
    additionalClass: {
      type: String,
      default: ''
    },
    type: {
      default: 'DDV',
      type: String
    },
    id: {
      default: 'NULL',
      type: String
    }
  },
  computed: {
    tabs() {
      return [
        { value: 1, translationKey: 'Webheader.annuaire_client' },
        { value: 2, translationKey: 'Webheader.annuaire_file' }
      ]
    },
    filterConfig() {
      if (this.tabName === 1) {
        // Configuration pour l'onglet 1 (Annuaire Client) - 5 filtres
        return [
          {
            id: 'annuaire-filename',
            type: 'text',
            prop: 'Name',
            labelKey: 'annuaire.file_name',
            icon: 'fa-solid fa-file',
            value: this.filterValues.Name,
            colSm: '6',
            colLg: '2'
          },
          {
            id: 'annuaire-bic',
            type: 'text',
            prop: 'BIC',
            labelKey: 'annuaire.bic',
            icon: 'fa-solid fa-building-columns',
            value: this.filterValues.BIC,
            colSm: '6',
            colLg: '2'
          },
          {
            id: 'annuaire-iban',
            type: 'text',
            prop: 'IBAN',
            labelKey: 'annuaire.iban',
            icon: 'fa-solid fa-credit-card',
            value: this.filterValues.IBAN,
            colSm: '6',
            colLg: '2'
          },
          {
            id: 'filter-start-date',
            type: 'datetime',
            prop: 'dateBegin',
            labelKey: 'filter.start_date',
            icon: 'fa-solid fa-calendar-days',
            value: this.filterValues.dateBegin,
            format: 'DD/MM/YYYY H:i:s',
            maxLength: 19,
            colSm: '6',
            colLg: '3'
          },
          {
            id: 'filter-end-date',
            type: 'datetime',
            prop: 'dateEnd',
            labelKey: 'filter.end_date',
            icon: 'fa-solid fa-calendar-days',
            value: this.filterValues.dateEnd,
            format: 'DD/MM/YYYY H:i:s',
            maxLength: 19,
            colSm: '6',
            colLg: '3'
          }
        ]
      } else {
        // Configuration pour l'onglet 2 (Fichier Annuaire) - 3 filtres
        return [
          {
            id: 'annuaire-name',
            type: 'text',
            prop: 'Name',
            labelKey: 'annuaire.file_name',
            icon: 'fa-solid fa-file',
            value: this.filterValues.Name,
            colSm: '12',
            colLg: '4'
          },
          {
            id: 'filter-start-date',
            type: 'datetime',
            prop: 'dateBegin',
            labelKey: 'filter.start_date',
            icon: 'fa-solid fa-calendar-days',
            value: this.filterValues.dateBegin,
            format: 'DD/MM/YYYY H:i:s',
            maxLength: 19,
            colSm: '6',
            colLg: '4'
          },
          {
            id: 'filter-end-date',
            type: 'datetime',
            prop: 'dateEnd',
            labelKey: 'filter.end_date',
            icon: 'fa-solid fa-calendar-days',
            value: this.filterValues.dateEnd,
            format: 'DD/MM/YYYY H:i:s',
            maxLength: 19,
            colSm: '6',
            colLg: '4'
          }
        ]
      }
    },
    buttonsListStyle () {
      return (!this.isSmartphoneOS) ? 'border: 1px solid #aaa;' : ''
    },
    fields () {
      let fields
      if (this.tabName == 1){
        fields = [
          { key: 'CreationDateRq1', label: this.$i18n.t('tracking.creation_date_request_1'), class: 'text-center', sortable: false },
          { key: 'CreationDateRp2', label: this.$i18n.t('tracking.creation_date_response_2'), class: 'text-center', sortable: false },
          { key: 'BICFrom', label: this.$i18n.t('tracking.bic_from'), class: 'text-center', sortable: false },
          { key: 'BICTo', label: this.$i18n.t('tracking.bic_to'), class: 'text-center', sortable: false },
          { key: 'IBAN', label: this.$i18n.t('tracking.iban'), class: 'text-center', sortable: false },
          { key: 'TestedData', label: this.$i18n.t('tracking.tested_data'), class: 'text-center', sortable: false },
          { key: 'Result', label: this.$i18n.t('tracking.maching_result'), class: 'text-center', sortable: false }
        ]
      }
      else{
        fields = [
          { key: 'CreationDateRq1', label: this.$i18n.t('tracking.creation_date_request_1'), class: 'text-center', sortable: false },
          { key: 'CreationDateRp2', label: this.$i18n.t('tracking.creation_date_response_2'), class: 'text-center', sortable: false },
          { key: 'BICFrom', label: this.$i18n.t('tracking.bic_from'), class: 'text-center', sortable: false },
          { key: 'BICTo', label: this.$i18n.t('tracking.bic_to'), class: 'text-center', sortable: false },
          { key: 'IBAN', label: this.$i18n.t('tracking.iban'), class: 'text-center', sortable: false },
          { key: 'Name', label: this.$i18n.t('tracking.name'), class: 'text-center', sortable: false },
          { key: 'AccountType', label: this.$i18n.t('tracking.account_type'), class: 'text-center', sortable: false },
        ]
      }

      return fields
    },
    isSmartphoneOS () {
      return this.$f7.device.os === 'android' || this.$f7.device.os === 'ios'
    },
    company_nameDirection () {
      return ((this.sortField === 'company_name' && this.sortDirection === 'ASC') ? 'fa-caret-up' : 'fa-caret-down')
    },
    statusVerificationDirection () {
      return ((this.sortField === 'statusVerification' && this.sortDirection === 'ASC') ? 'fa-caret-up' : 'fa-caret-down')
    },
    activeDirection () {
      return ((this.sortField === 'active' && this.sortDirection === 'ASC') ? 'fa-caret-up' : 'fa-caret-down')
    },
    hasInputFilter () {
      if (
          this.filterValues.IBAN ||
          this.filterValues.BIC ||
          this.filterValues.dateBegin || this.filterValues.dateEnd) {
        return true
      }
      return false
    }
  },
  data () {
    return {
      activateSpinner: false,
      displayFilter: false,
      filterBy: '',
      hasDefaultFilter: false,
      idTrackAcc: '',
      statusVerification: '',
      mode: 1,
      tabName: 1,
      identifier: '',
      technicalStatus: '',
      filterValues: {
        BIC: '',
        IBAN: '',
        Name: '',
        dateBegin: '',
        dateEnd: ''
      },
      returnUrl: '',
      noMore: false,
      pageCounter: 0,
      paginationObject: {},
      randomId: Math.random().toString(36).substr(2, 10),
      requestsNumber: '',
      scrollFlag: true,
      sortDirection: 'DESC',
      sortField: 'dateAction',
      trackActive: '',
      TracksList: [],
      TrackType: ''
    }
  },
  created () {
    if (this.id !== 'NULL'){
      this.tabName = parseInt(this.id)
    }
    this.$bus.$emit('switch-selected-menu', '/annuaire/NULL')
  },
  mounted () {
    this.$$('.infinite-scroll-preloader').remove()
  },
  beforeDestroy () {
    let that = this
    that.$engine.firstOn().tracking.cleanFilterCondition()
  },
  methods: {
    canAddAnnuaireFile () {
      return this.$engine.firstOn().rights.rightIs('annuaire_file_add')
    },
    addAnnuaireFile () {
      let that = this
      loadPage(that, {url: '/annuaireform/create/'})
    },
    changeTabName (tabName) {
      this.tabName = tabName
      // Réinitialiser les filtres lors du changement d'onglet
      this.clearFilterFields()
    },
    toggleFilter () {
      this.displayFilter = !this.displayFilter
    },
    refreshAnnuaire () {
      this.refreshInbox()
    },
    refreshInbox () {
      // Émettre un événement de rafraîchissement aux composants enfants
      this.$bus.$emit('refresh-annuaire-list')
    },
    onFilterChanged ({ prop, value }) {
      this.filterValues[prop] = value
    },
    applyFilter () {
      // Émettre un événement pour appliquer les filtres
      this.$bus.$emit('apply-annuaire-filters', this.filterValues)
    },
    clearFilterFields () {
      this.filterValues.Name = ''
      this.filterValues.BIC = ''
      this.filterValues.IBAN = ''
      this.filterValues.dateBegin = ''
      this.filterValues.dateEnd = ''
      // Réappliquer les filtres vides
      this.applyFilter()
    }
  }
}
</script>

<style scoped>
/* Styles pour les tabs en version desktop */
::v-deep .nav-tabs .nav-link {
  border: 1px solid #dee2e6;
  background-color: #f8f9fa;
  color: #495057;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

::v-deep .nav-tabs .nav-link.active {
  background-color: var(--actionButtonColor, #0d7e7d) !important;
  color: white !important;
  border-color: var(--actionButtonColor, #0d7e7d) !important;
  font-weight: bold;
}

::v-deep .nav-tabs .nav-link:hover:not(.active) {
  background-color: rgba(13, 126, 125, 0.1);
  border-color: rgba(13, 126, 125, 0.3);
  color: #0d7e7d;
}

/* Style focus pour l'accessibilité */
::v-deep .nav-tabs .nav-link:focus {
  box-shadow: 0 0 0 0.25rem rgba(17, 176, 175, 0.25);
  outline: none;
}

/* Espacement pour Bootstrap 4 (qui n'a pas les classes g-* natives) */
.g-3 > [class*="col-"] {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  margin-bottom: 1rem;
}

.g-3 {
  margin-right: -0.5rem;
  margin-left: -0.5rem;
}

/* Espacement horizontal pour les boutons */
.gx-2 > [class*="col-"] {
  padding-right: 0.25rem;
  padding-left: 0.25rem;
}

.gx-2 {
  margin-right: -0.25rem;
  margin-left: -0.25rem;
}

/* Amélioration de l'espacement des cartes de filtres */
.bg-light.border.rounded.p-3 {
  height: 100%;
}

/* Animation du collapse */
::v-deep .collapse {
  transition: height 0.35s ease;
}

/* Espacement vertical additionnel sur mobile */
@media (max-width: 576px) {
  .g-3 > [class*="col-"] {
    margin-bottom: 1.5rem;
  }
}

/* Force la couleur noire pour les labels de filtres pour un meilleur contraste RGAA */
.form-label.text-dark {
  color: #212529 !important;
}
</style>
